import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { Address } from 'viem'
import { postQuote } from '../api/client'
import type { QuoteResponse } from '../api/types'
import { QUOTE_REFRESH_MS } from '../lib/config'

export type QuoteParams = {
  tokenIn: Address | null
  tokenOut: Address | null
  /** Raw token units. Null while the input is empty or unparseable. */
  amount: bigint | null
}

export type UseQuoteResult = UseQueryResult<QuoteResponse, Error> & {
  /** True while a *new* pair/amount is being quoted, as opposed to a background refresh. */
  isInitialLoad: boolean
  /**
   * The quote for the pair and amount currently on screen, or undefined while one is being
   * fetched.
   *
   * Not the same as `data`. `placeholderData: keepPreviousData` keeps the last answer
   * visible so a background refresh does not blank the card — but it also means that right
   * after the user changes a token or an amount, `data` still holds the *previous* trade's
   * quote. Rendering that, or worse letting it reach the swap button, would show one trade
   * and sign another. React Query flags it (`isPlaceholderData`) and this is that flag
   * applied: same key, keep it; different key, there is no quote yet.
   */
  current: QuoteResponse | undefined
}

/**
 * The live quote.
 *
 * Every number the swap card shows comes from here. There is no local fallback and there
 * must not be one: `pool.fee` and `globalState().lastFee` do not report what an Algebra pool
 * on this chain actually charges — measured at 100, 1574 and 3000 hundredths of a bp against
 * a uniform `lastFee = 500` — so a browser-side AMM simulation would be wrong by up to 6x
 * with nothing to indicate it. `OmegaQuoter`, through this API, is the only source.
 *
 * It refreshes on a timer because the plan behind it expires. `routeId.ttlMs` is 30s and
 * runs from when the plan was *quoted*, so a stale card is not just a stale price — it is a
 * route id that `/swap` will refuse. Refetching in the background keeps the id the user is
 * about to spend younger than its window.
 */
export function useQuote(params: QuoteParams): UseQuoteResult {
  const { tokenIn, tokenOut, amount } = params
  const enabled = Boolean(tokenIn && tokenOut && amount && amount > 0n && tokenIn !== tokenOut)

  const query = useQuery<QuoteResponse, Error>({
    queryKey: ['quote', tokenIn, tokenOut, amount?.toString() ?? null],
    enabled,
    queryFn: ({ signal }) =>
      postQuote(
        {
          tokenIn: tokenIn as Address,
          tokenOut: tokenOut as Address,
          amount: (amount as bigint).toString(),
          tradeType: 'exactIn',
        },
        signal
      ),
    refetchInterval: QUOTE_REFRESH_MS,
    // A backgrounded tab quotes nothing — it would spend node budget on a card nobody is
    // reading — and refetches the moment it comes back, so a returning user never acts on
    // a quote whose route id expired while they were away.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  })

  return {
    ...query,
    isInitialLoad: enabled && (query.isPending || query.isPlaceholderData),
    current: query.isPlaceholderData ? undefined : query.data,
  }
}
